import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

type Flags = Record<string, unknown>;

/**
 * Subset of `window.App.Features` the pre-approvals eligibility check reads.
 * Declared locally (rather than via a global `Window.App` augmentation) so the
 * shared library stays self-contained and doesn't clash with the differing
 * `Window.App` declarations each consuming MFE ships in its own `global.d.ts`.
 */
export interface PreApprovalsFeatureGates {
    EnableFinancingPreApprovals?: boolean;
    EnableGreenskyIntegration?: boolean;
    EnableSynchronyIntegration?: boolean;
}

/**
 * Computes the ordered list of pre-approvals providers that qualify for the
 * current tenant per the feature-gate + LD-flag rule.
 *
 * A provider can used for pre-approvals only if:
 *  * Feature gate "Enable Financing Pre-approvals" is True
 *  * The provider's "Enable (provider name) Integration" is True
 *
 * Temporary: FG "Enable Financing Pre-approvals" will be released as part of
 * 81yildun. Until then, if this FG is off, we'll also show the provider if
 * the provider's pre-approval LD flag is on. We can remove that after 81yildun
 * is released.
 */
export const getPreApprovalsEligibleProviders = (
    flags: Flags,
    features: PreApprovalsFeatureGates | undefined,
): string[] => {
    const result: string[] = [];
    const preApprovalsGate = features?.EnableFinancingPreApprovals;

    if (features?.EnableGreenskyIntegration && (preApprovalsGate ?? flags.finPreApprovalsGs)) {
        result.push('GreenSky');
    }
    if (
        features?.EnableSynchronyIntegration &&
        (preApprovalsGate ?? flags.finPreApprovalsSynchrony)
    ) {
        result.push('Synchrony');
    }
    return result;
};

/**
 * Hook form of {@link getPreApprovalsEligibleProviders}. Wires the LD flag
 * source together with the non-reactive `window.App.Features` feature gates
 * (set by the host before MFE bootstrap and static for the page lifetime), so
 * callers can't forget to pass the feature gates.
 */
export const usePreApprovalsEligibleProviders = (): string[] => {
    const flags = useFlags();
    return useMemo(() => {
        const features = (window as { App?: { Features?: PreApprovalsFeatureGates } }).App
            ?.Features;
        return getPreApprovalsEligibleProviders(flags, features);
        // `window.App.Features` is a non-reactive global; only `flags` drives recomputation.
    }, [flags]);
};
